/**
 * Apple-silicon detection is genuinely unreliable from a browser: Safari and Chrome both
 * report `Intel Mac OS X` in the UA on an M-series Mac, so a UA sniff would send every Mac
 * user to the Intel build. We therefore report `universal` for macOS unless the UA says
 * arm64 outright, and let the *manifest* decide — the portal lists both slices with their
 * architecture on the label, which is honest about what we cannot know.
 */
export function detectPlatform(hints = {}) {
    const ua = (hints.userAgent ?? '').toLowerCase();
    const navigatorPlatform = (hints.platform ?? '').toLowerCase();
    const haystack = `${ua} ${navigatorPlatform}`;
    const arch = /aarch64|arm64/.test(haystack) ? 'arm64' : 'x64';
    if (/windows|win32|win64/.test(haystack))
        return { platform: 'win', arch };
    // Checked before the generic mac test: iOS reports "Mac OS X" too, and there is no iOS
    // installer to offer.
    if (/iphone|ipad|ipod|android/.test(haystack))
        return null;
    if (/mac os x|macintosh|darwin/.test(haystack)) {
        return { platform: 'mac', arch: /aarch64|arm64/.test(haystack) ? 'arm64' : 'universal' };
    }
    if (/linux|x11/.test(haystack))
        return { platform: 'linux', arch };
    return null;
}
/** Reads the hints off the ambient `navigator`, if there is one. */
export function detectCurrentPlatform() {
    const nav = globalThis.navigator;
    if (!nav)
        return null;
    return detectPlatform({ userAgent: nav.userAgent, platform: nav.platform });
}
const PLATFORM_LABELS = {
    mac: 'macOS',
    win: 'Windows',
    linux: 'Linux',
};
export function platformLabel(platform) {
    return PLATFORM_LABELS[platform];
}
/** Human-readable size for a download button. Binary units, one decimal past a megabyte. */
export function formatBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    const mb = bytes / 1024 / 1024;
    if (mb < 1)
        return `${Math.round(bytes / 1024)} KB`;
    if (mb < 1024)
        return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
}
//# sourceMappingURL=platform.js.map